import EngineCleanupBase from '../../../../services/engines/EngineCleanupBase';
import ComponentNotifier from '../../../../services/wss/ComponentNotifier';
import topicGroupSearchCache from './TopicGroupSearchCache';
import TopicGroupSearchFetcher from './TopicGroupSearchFetcher';
import prioUpdater from '../../../../components/shared/PrioUpdater';

const _name = 'TopicGroupSearchEngine';
const DEBOUNCE_MS = 300;

/**
 * Manages search state scoped to the active TopicGroup service.
 * Cache is cleared on service change or explicit reset.
 * Search is debounced — query updates instantly for UI feedback,
 * actual fetch fires DEBOUNCE_MS after the last keystroke.
 * Singleton — shared across the dialog lifecycle.
 */
class TopicGroupSearchEngine extends EngineCleanupBase {
    _fetcher = new TopicGroupSearchFetcher();
    _service = null;
    _loading = false;
    _query = '';
    _debounceTimer = null;

    bootup = () => {
        this.initialize(_name);
    };

    /**
     * Sets the active service scope. Clears previous results.
     * @param {string} service
     */
    setService = (service) => {
        try {
            this._clearDebounce();
            this._service = service;
            this._query = '';
            topicGroupSearchCache.clear();
            this._notify();
        } catch (error) {
            this.logError('setService', error.message);
        }
    };

    /**
     * Updates query instantly (UI feedback), debounces the actual fetch.
     * @param {string} query
     */
    search = (query) => {
        try {
            this._query = query;
            this._clearDebounce();

            if (!query || query.trim().length < 3) {
                topicGroupSearchCache.clear();
                this._notify();
                return;
            }

            this._notify();
            this._debounceTimer = setTimeout(() => this._executeSearch(query), DEBOUNCE_MS);
        } catch (error) {
            this.logError('search', error.message);
        }
    };

    /** @returns {object[]} */
    getResults = () => {
        return topicGroupSearchCache.getAll();
    };

    /** @returns {boolean} */
    isLoading = () => this._loading;

    /** @returns {string} */
    getQuery = () => this._query;

    /**
     * Toggles isPinned on an item — saves via prioUpdater, then optimistically
     * updates the local cache so the UI reflects the change without a re-fetch.
     * @param {object} item
     */
    togglePin = async (item) => {
        try {
            const updated = { ...item, isPinned: !item.isPinned };
            topicGroupSearchCache.addOrUpdate([updated]);
            this._notify();
            await prioUpdater.save(updated);
        } catch (error) {
            this.logError('togglePin', error.message);
        }
    };

    clear() {
        this._clearDebounce();
        topicGroupSearchCache.clear();
        this._service = null;
        this._query = '';
        this._loading = false;
        super.clear();
    }

    // ─── private ──────────────────────────────────────────

    _executeSearch = async (query) => {
        try {
            this._loading = true;
            this._notify();

            const items = await this._fetcher.search(query.trim(), this._service);
            topicGroupSearchCache.setCache(items);
        } catch (error) {
            this.logError('_executeSearch', error.message);
        } finally {
            this._loading = false;
            this._notify();
        }
    };

    _clearDebounce = () => {
        if (this._debounceTimer) {
            clearTimeout(this._debounceTimer);
            this._debounceTimer = null;
        }
    };

    _notify = () => {
        ComponentNotifier.notify(_name, 'dataChanged', {
            results: this.getResults(),
            loading: this._loading,
            query: this._query,
        });
    };
}

const topicGroupSearchEngine = new TopicGroupSearchEngine();
export default topicGroupSearchEngine;
